import { useEffect, useRef } from "react";
import { useParams } from "react-router";
import { Helmet } from "react-helmet";
import { shallowEqual, useSelector } from "react-redux";

import { getSelectedSection } from "redux/Metadata/selectors";
// import { FilterOption } from "types";
import vlIcon from "assets/img/newVLicon.svg";
import EnrichmentIcon from "assets/icons/EnrichmentIcon";

import EnrichmentModelsSection from "./Sections/EnrichmentModels";
import MetadataSection from "./Sections/Metadata";
import UserTagsSection from "./Sections/UserTags";
// import CaptionSection from "./Sections/Captions";
import QualitySection from "./Sections/Quality";
import LabelSection from "./Sections/Labels";

import {
  // getCaptions,
  getMetadataSummary,
  getDatasetEnrichmentModels,
  getIsCardslistLoading,
  getIsEnrichmentModelsEnabled,
  getNavigationClusterLoader,
  getDatasetExternalSources,
  getMetadataSummaryLoading,
} from "redux/SingleDataset/selectors";
import classNames from "classnames";

import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

interface MetadataSummaryProps {
  showMetadataSummary: boolean;
}

const MetadataSummary = ({ showMetadataSummary }: MetadataSummaryProps) => {
  const { datasetId, clusterId } = useParams();
  const queryParams = useAllQueryParamsFilters();
  const isDataPage = !!datasetId && !clusterId;

  // const captions = useSelector(getCaptions);
  const selectedSection = useSelector(getSelectedSection);
  const isCardsListLoading = useSelector(getIsCardslistLoading);
  const isNavigationClusterLoading = useSelector(getNavigationClusterLoader);
  const isEnrichmentModelsEnabled = useSelector(getIsEnrichmentModelsEnabled);

  const externalSources = useSelector(
    (state) => getDatasetExternalSources(state, queryParams),
    shallowEqual
  );
  const enrichmentModels = useSelector(
    (state) => getDatasetEnrichmentModels(state, queryParams),
    shallowEqual
  );
  const metadataSummary = useSelector(
    (state) => getMetadataSummary(state, queryParams, isDataPage),
    shallowEqual
  );
  const metadataSummaryLoading = useSelector(
    (state) => getMetadataSummaryLoading(state, queryParams, isDataPage),
    shallowEqual
  );

  const dataLoading = isDataPage
    ? isCardsListLoading
    : isNavigationClusterLoading;
  const isLoading = metadataSummaryLoading || dataLoading;

  const showVLEnrichment =
    isEnrichmentModelsEnabled && !!enrichmentModels.length;

  const parentContainerRef = useRef<HTMLDivElement>(null);
  const vlEnrichmentRef = useRef<HTMLDivElement>(null);
  const metadataRef = useRef<HTMLDivElement>(null);
  const userTagsRef = useRef<HTMLDivElement>(null);
  // const captionsRef = useRef<HTMLDivElement>(null);
  const qualityRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);

  const sectionHeight =
    !!parentContainerRef.current &&
    parentContainerRef.current.offsetHeight - 50;

  const metaDataSummaryWrapperClassNames = classNames({
    [styles.metaDataSummaryWrapper]: showMetadataSummary,
    [styles.hiddenDiv]: !showMetadataSummary,
  });


  useEffect(() => {
    const parent = parentContainerRef.current;
    const vlEnrichment = vlEnrichmentRef.current;
    const metadata = metadataRef.current;
    const userTag = userTagsRef.current;
    const quality = qualityRef.current;
    // const caption = captionsRef.current;
    const labels = labelsRef.current;

    if (
      parent &&
      vlEnrichment &&
      metadata &&
      userTag &&
      quality &&
      // caption &&
      labels
    ) {
      switch (selectedSection) {
        case "vl-enrichment":
          labels.style.display = "none";
          // caption.style.display = "none";
          quality.style.display = "none";
          userTag.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.height = `${parent.clientHeight}px`;
          break;
        case "metadata":
          labels.style.display = "none";
          userTag.style.display = "none";
          quality.style.display = "none";
          // caption.style.display = "none";
          vlEnrichment.style.display = "none";
          metadata.style.height = `${parent.clientHeight}px`;
          break;
        case "user-tags":
          labels.style.display = "none";
          quality.style.display = "none";
          // caption.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          userTag.style.height = `${parent.clientHeight}px`;
          break;
        case "quality":
          labels.style.display = "none";
          userTag.style.display = "none";
          // caption.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          quality.style.height = `${parent.clientHeight}px`;
          break;
        case "captions":
          labels.style.display = "none";
          quality.style.display = "none";
          userTag.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          // caption.style.height = `${parent.clientHeight}px`;
          break;

        case "labels":
          quality.style.display = "none";
          userTag.style.display = "none";
          // caption.style.display = "none";
          metadata.style.display = "none";
          vlEnrichment.style.display = "none";
          labels.style.height = `${parent.clientHeight}px`;
          break;

        case null:
          vlEnrichment.style.display = "unset";
          metadata.style.display = "unset";
          userTag.style.display = "unset";
          quality.style.display = "unset";
          // caption.style.display = "unset";
          labels.style.display = "unset";

          vlEnrichment.style.height = "max-content";
          metadata.style.height = "max-content";
          userTag.style.height = "max-content";
          quality.style.height = "max-content";
          // caption.style.height = "max-content";
          labels.style.height = "max-content";
          break;

        default:
          return;
      }
    }
  }, [selectedSection]);

  return (
    <div ref={parentContainerRef} className={metaDataSummaryWrapperClassNames}>
      <Helmet>
        <link rel="preload" href={vlIcon} as="image" type="image/svg+xml" />
      </Helmet>
      <div className={styles.expandedContainer}>
        <EnrichmentModelsSection
          isLoading={isLoading}
          models={enrichmentModels}
          sources={externalSources}
          vlEnrichmentRef={vlEnrichmentRef}
          showVLEnrichment={showVLEnrichment}
          icon={<EnrichmentIcon />}
          sectionHeight={selectedSection === "vl-enrichment" && sectionHeight}
        />

        <QualitySection
          isLoading={isLoading}
          qualityRef={qualityRef}
          issueTypes={metadataSummary!.issueTypes}
          sectionHeight={selectedSection === "quality" && sectionHeight}
        />

        <LabelSection
          labels={metadataSummary!.mediaLabels}
          labelsRef={labelsRef}
          sectionHeight={selectedSection === "labels" && sectionHeight}
          isLoading={isLoading}
        />

        {/* <CaptionSection
          captions={captions}
          isLoading={isLoading}
          captionsRef={captionsRef}
          sectionSearchIcon={renderSectionSearchIcon(
            "captions",
            captions.length
          )}
          sectionHeight={selectedSection === "captions" && sectionHeight}
        /> */}

        <UserTagsSection
          isLoading={isLoading}
          userTagsRef={userTagsRef}
          userTags={metadataSummary!.userTags}
          sectionHeight={selectedSection === "user-tags" && sectionHeight}
        />

        <MetadataSection
          isLoading={isLoading}
          metadataRef={metadataRef}
          sources={externalSources}
          models={enrichmentModels}
          videoData={metadataSummary?.videoData}
          sectionHeight={selectedSection === "metadata" && sectionHeight}
        />
      </div>
    </div>
  );
};

export default MetadataSummary;
